import { debounce, isEqual, pick } from "lodash"
import type { Router } from "next/router"
import React from "react"

import type { BookingSearch } from "@/lib/bookingSearch"
import type { Geolocation } from "@/lib/geolocation"
import { debugEnabled } from "@/lib/routing/debugEnabled"
import type { Services } from "@/lib/services"
import { serviceLabelEntries } from "@/lib/services/constants"

import { BookingSearchAddressInput } from "@/components/Booking/BookingSearchAddressInput"
import type { BookingSearchAddressInputProps } from "@/components/Booking/BookingSearchAddressInput/component"
import IconButton from "@/components/common/Buttons/IconButton"
import { Divider } from "@/components/common/Divider/Divider"
import { Select } from "@/components/common/Select/Select"

import { SearchIcon } from "@/constants/icons"

export type BookingSearchProps = {
  bookingSearch: BookingSearch.ContextData
  geolocation: Geolocation.ContextData
  htmlForSearch?: string
  htmlForSpecialty?: string
  htmlForTimeframe?: string
  isSmallDevice: boolean | undefined
  onLocationResolve?: BookingSearchAddressInputProps[`onLocationResolve`]
  onSearchChange: (searchValue: string) => void
  onTimeframeChange: (timeframe: string) => void
  router: Router
  searchValue?: string
  services: Services.ContextData
  timeframeValue?: string
}

type State = {
  addressInputWidth: number
  height: number
  width: number
}

type BookingQueryPropKeys = `timeframeValue` | `searchValue`

type BookingQueryProps = Pick<BookingSearchProps, BookingQueryPropKeys>

export class BookingSearchComponent extends React.PureComponent<
  BookingSearchProps,
  State
> {
  private containerRef?: HTMLDivElement

  public state = {
    addressInputWidth: 0,
    height: 0,
    width: 0,
  }

  private getQueryProps = (props: BookingSearchProps): BookingQueryProps => {
    return pick(props, [`timeframeValue`, `searchValue`])
  }

  private debouncedQueryProps: BookingQueryProps = this.getQueryProps(
    this.props
  )

  public componentDidUpdate = async () => {
    this.syncContainerWidth()
    await this.syncBookingQuery()
  }

  public componentDidMount = () => {
    this.syncContainerWidth()
  }

  public componentWillUnmount = () => {
    this.syncBookingQuery.cancel()
    this.syncContainerWidth.cancel()
  }

  private onSpecialityChange = (specialty: Services.Option) => {
    this.props.services.selectService({ service: specialty })
  }

  private onAddressResolved = async (result?: {
    address: Geolocation.Bing.Address
    position: Geolocation.Position
  }): Promise<boolean> => {
    const address = result?.address || this.props.geolocation.address
    const position = result?.position || this.props.geolocation.position

    if (address && position) {
      if (this.props.onLocationResolve) {
        await this.props.onLocationResolve({ address, position })
        return true
      } else {
        return this.props.router.push({
          pathname: `/booking`,
          query: {
            position,
            search: address.formattedAddress,
            specialty: this.props.services.selected,
            timeframe: this.props.timeframeValue,
          },
        })
      }
    }

    return false
  }

  private onSubmit = async () => {
    const addressResolved = await this.onAddressResolved()

    if (addressResolved || this.props.geolocation.permissionDenied) {
      return
    }

    const conclusion = await this.props.geolocation.useMyLocation()
    const { address, position } = conclusion.result || {}

    if (address && position) {
      await this.onAddressResolved({ address, position })
    } else {
      if (debugEnabled(this.props.router)) {
        console.log(
          `BookingSearch#onSubmit: Use my location failed.`,
          conclusion
        )
      }
    }
  }

  public render = () => {
    const {
      htmlForSearch,
      htmlForSpecialty,
      htmlForTimeframe,
      isSmallDevice,
      onLocationResolve,
      onSearchChange,
      onTimeframeChange,
      searchValue,
      timeframeValue,
    } = this.props

    return (
      <div
        className={`flex items-center overflow-hidden rounded-full border border-gray-200 bg-white pl-2 shadow-sm`}
        ref={(containerRef) => {
          if (containerRef) {
            this.containerRef = containerRef
          }
        }}
      >
        {(!isSmallDevice || isSmallDevice !== undefined) && (
          <>
            <Select
              wrapperClassName={`w-32 sm:block hidden`}
              onValueChange={onTimeframeChange}
              defaultValue={timeframeValue || `next 7 days`}
              value={timeframeValue}
              ariaLabel={`timeframe`}
              htmlFor={(htmlForTimeframe && htmlForTimeframe) || `timeframe`}
              labelID={
                (htmlForTimeframe && `${htmlForTimeframe}-label`) ||
                `timeframe-label`
              }
            >
              {[`Next 7 days`, `Next 3 days`].map((f, i) => {
                return (
                  <Select.Item key={`${f}-${i}`} value={f.toLowerCase()}>
                    {f}
                  </Select.Item>
                )
              })}
            </Select>
            <Divider
              flexChild
              className={`my-2 hidden sm:block`}
              orientation={`vertical`}
              thickness={1}
            />
          </>
        )}

        <Select
          wrapperClassName={`w-36`}
          onValueChange={this.onSpecialityChange}
          value={this.props.services.selected}
          htmlFor={htmlForSpecialty || `specialty`}
          ariaLabel={`specialty`}
          labelID={
            (htmlForSpecialty && `${htmlForSpecialty}-label`) ||
            `specialty-label`
          }
        >
          {serviceLabelEntries.map(([serviceOption, label]) => {
            return (
              <Select.Item key={serviceOption} value={serviceOption}>
                {label}
              </Select.Item>
            )
          })}
        </Select>
        <Divider
          flexChild
          className={`my-2`}
          orientation={`vertical`}
          thickness={1}
        />
        <BookingSearchAddressInput
          className={`flex-1`}
          // These magic numbers affect the size and position of the dropdown for
          // mobile vs desktop devices. I reached them through experimental fiddling,
          // and I'm not 100% sure why they work. Tested on Chrome, Firefox, and Safari.
          dropdownAlignOffset={
            isSmallDevice
              ? 2 - this.state.height / 2
              : -(4 + this.state.height / 4)
          }
          dropdownWidth={
            isSmallDevice
              ? this.state.width - (2 + (this.state.height * 3) / 4)
              : this.state.addressInputWidth - this.state.height / 2
          }
          htmlForSearch={htmlForSearch}
          onLocationResolve={onLocationResolve}
          onSearchChange={onSearchChange}
          searchValue={
            searchValue || this.props.geolocation.address?.formattedAddress
          }
          syncWidth={this.syncAddressInputWidth}
        >
          <IconButton
            id={`submit-search`}
            ariaLabel={`Search for bookings`}
            buttonSize={`medium`}
            className={`mr-1.5`}
            onClick={this.onSubmit}
            icon={SearchIcon}
          />
        </BookingSearchAddressInput>
      </div>
    )
  }

  private syncAddressInputWidth: BookingSearchAddressInputProps[`syncWidth`] =
    ({ width }) => {
      if (width !== this.state.addressInputWidth) {
        this.setState({ addressInputWidth: width })
      }
    }

  private syncBookingQuery = debounce(async (): Promise<void> => {
    const queryProps = this.getQueryProps(this.props)

    if (isEqual(queryProps, this.debouncedQueryProps)) {
      return
    }

    this.debouncedQueryProps = queryProps

    if (window.location.pathname === `/booking`) {
      await this.props.router.push(
        {
          pathname: `/booking`,
          query: {
            specialty: this.props.services.selected,
            timeframe: this.props.timeframeValue,
            search: this.props.searchValue,
          },
        },
        undefined,
        {
          shallow: true,
        }
      )
    }
  }, 500)

  private syncContainerWidth = debounce(
    () => {
      if (this.containerRef) {
        const { height, width } = this.containerRef.getBoundingClientRect()
        if (width !== this.state.width || height !== this.state.height) {
          this.setState({ height, width })
        }
      }
    },
    350,
    { leading: true, trailing: true }
  )
}
